import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { scrub, sentryEnabled } from "@/lib/sentry";

interface ErrorLogEntry {
  message: string;
  stack?: string;
  source?: "frontend" | "backend" | "edge_function";
  severity?: "warning" | "error" | "critical";
  url?: string;
  metadata?: Record<string, unknown>;
}

/** Debounce duplicate errors within 5 seconds */
const recentErrors = new Map<string, number>();
const DEDUP_MS = 5000;

function isDuplicate(key: string): boolean {
  const now = Date.now();
  const last = recentErrors.get(key);
  if (last && now - last < DEDUP_MS) return true;
  recentErrors.set(key, now);
  // Cleanup old entries
  if (recentErrors.size > 50) {
    for (const [k, t] of recentErrors) {
      if (now - t > DEDUP_MS) recentErrors.delete(k);
    }
  }
  return false;
}

function toSentryLevel(severity?: ErrorLogEntry["severity"]): "warning" | "error" | "fatal" {
  if (severity === "warning") return "warning";
  if (severity === "critical") return "fatal";
  return "error";
}

export async function logError(entry: ErrorLogEntry): Promise<void> {
  const dedupKey = `${entry.message}::${entry.source ?? "frontend"}`;
  if (isDuplicate(dedupKey)) return;

  if (sentryEnabled) {
    const error = new Error(entry.message);
    if (entry.stack) error.stack = entry.stack;
    Sentry.captureException(error, {
      level: toSentryLevel(entry.severity),
      tags: { source: entry.source ?? "frontend" },
      extra: scrub({ url: entry.url, ...entry.metadata }),
    });
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id ?? null;

    // Get business_id from profile if logged in
    let businessId: string | null = null;
    if (userId) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("business_id")
        .eq("id", userId)
        .single();
      businessId = profile?.business_id ?? null;
    }

    await (supabase as any).from("error_logs").insert({
      message: entry.message.slice(0, 2000),
      stack: entry.stack?.slice(0, 5000) ?? null,
      source: entry.source ?? "frontend",
      severity: entry.severity ?? "error",
      url: entry.url ?? (typeof window !== "undefined" ? window.location.href : null),
      metadata: entry.metadata ?? {},
      user_id: userId,
      business_id: businessId,
      resolved: false,
    });
  } catch {
    // Silently fail – don't create error loops
  }
}
